<template>
  <div class="kpt-detail-content-page">
    <vue-headful :title="`${remote.name} - Usage`" />
    <div class="kpt-detail-content-actions">
      <div class="buttons">
        <b-button type="is-primary is-outlined" size="is-small" icon-left="file-excel" :disabled="!satUsage && !cellUsage" :title="!satUsage && !cellUsage ? 'Export not available when no data is found.' : ''" @click="exportGraph">EXPORT DAILY</b-button>
      </div>
    </div>
    <div class="kpt-remote-usage-container">
      <div>
        <b-datepicker v-model="selectedDate"
                      type="month"
                      placeholder="Click to select..."
                      icon="calendar"
                      @input="valueChanged"
                      trap-focus>
        </b-datepicker>
        <hr>
        <div v-if="activeSubscriptionsForTheSelectedMonth.length">
          <div class="selected-sub" v-for="(activeSub, idx) in activeSubscriptionsForTheSelectedMonth" :key="idx">
            <detail-field>
              <template v-slot:label>
                Product
              </template>
              <template v-slot:value>
                {{activeSub.productName}}
              </template>
            </detail-field>
            <detail-field>
              <template v-slot:label>
                Region
              </template>
              <template v-slot:value>
                {{activeSub.cellRegionName || activeSub.satRegionName}}
              </template>
            </detail-field>
            <detail-field>
              <template v-slot:label>
                Start Date
              </template>
              <template v-slot:value>
                {{format(new Date(activeSub.startDate), 'yyyy-MM-dd')}}
              </template>
            </detail-field>
            <detail-field>
              <template v-slot:label>
                End Date
              </template>
              <template v-slot:value>
                {{format(new Date(activeSub.endDate), 'yyyy-MM-dd')}}
              </template>
            </detail-field>
            <hr>
            <detail-field>
              <template v-slot:label>
                Used Sat
              </template>
              <template v-slot:value>
                <span v-if="activeSub" :class="ifUsageExceedsLimit(usedSatData, activeSub.dataLimitInGb)">{{$numeral(usedSatData).format('0.00 b')}}</span> / {{activeSub.dataLimitInGb}} GB
              </template>
            </detail-field>
            <detail-field>
              <template v-slot:label>
                Used Cell
              </template>
              <template v-slot:value>
                <span v-if="activeSub" :class="ifUsageExceedsLimit(usedCellData, activeSub.dataLimitInGb)">{{$numeral(usedCellData).format('0.00 b')}}</span> / {{activeSub.dataLimitInGb}} GB
              </template>
            </detail-field>
          </div>
        </div>
        <div v-else>
          <p>No subscriptions found for this month.</p>
        </div>
      </div>
      <div v-if="selectedDate">
        <label class="label">Remote Usage</label>
        <div class="kpt-chart-container">
          <b-loading :is-full-page="false" :active.sync="loadingUsage" :canCancel="false"></b-loading>
          <chart ref="usageGraph"></chart>
          <div class="kpt-chart-no-data-overlay" v-if="!satUsage && !cellUsage"></div>
          <div class="kpt-chart-no-data-text" v-if="!satUsage && !cellUsage"><strong>No data found for the selected month.</strong></div>
        </div>
        <hr>
        <b-table :data="monthlyUsage"
                 :narrowed="true"
                 :hoverable="true"
                 :loading="isLoadingMonthlyUsage"
                 sort-icon="chevron-up"
                 sort-icon-size="is-small"
                 class="has-horizontal-scroll is-full-height"
                 default-sort="name"
                 default-sort-direction="desc"
                 @sort="onSort">
            <b-table-column v-slot="props" field="name" width="80" label="Month" sortable>
              {{ props.row.timestamp }}
            </b-table-column>
            <b-table-column v-slot="props" field="satUsage" width="140" label="Sat Usage" sortable>
              {{ $numeral(props.row.satUsage).format('0.0 b') || 0 }}
            </b-table-column>
            <b-table-column v-slot="props" field="cellUsage" width="140" label="Cell Usage" sortable>
              {{ $numeral(props.row.cellUsage).format('0.0 b') || 0 }}
            </b-table-column>
            <b-table-column v-slot="props" field="subscriptions" label="Subscriptions">
              <ul>
                <li :title="`Start: ${format(new Date(sub.startDate), 'yyyy-MM-dd')} | End: ${format(new Date(sub.endDate), 'yyyy-MM-dd')}`" v-for="sub in props.row.subscriptions" :key="sub.id"><router-link :to="{ name: 'subscriptionOverview', params: { id: sub.id }}">{{sub.name}}</router-link></li>
              </ul>
            </b-table-column>
          <template slot="empty">
            <p v-if="!isLoadingMonthlyUsage">No usage history found.</p>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import {
  lastDayOfMonth,
  format,
  getDaysInMonth,
  endOfMonth,
  isAfter,
  isBefore,
  subMonths
} from 'date-fns';
import numeral from 'numeral';
import Chart from '../../shared/ChartComponent.vue';
import { idToNameParserMixin } from '../../shared/mixins/idToNameParser';
import { exportCsvMixin } from '../../shared/mixins/exportCsvMixin';
import { dateUtilityMixin } from '../../shared/mixins/dateUtilityMixin';
import { shortenProductNameMixin } from '../../shared/mixins/shortenProductNameMixin';

export default {
  data() {
    return {
      selectedDate: new Date(),
      format,
      graphSettings: {
        data: {
          x: 'x',
          xFormat: '%Y-%m-%d %H:%M:%S', // how the date is parsed
          columns: [],
          type: 'bar',
          colors: {
            Satellite: '#75A7D3',
            Cellular: '#FC800E'
          },
          groups: [
            ['Satellite', 'Cellular']
          ],
        },
        bar: {
          width: {
            ratio: 0.5
          }
        },
        axis: {
          x: {
            type: 'timeseries',
            tick: {
              format: '%d', // how the date is displayed
              culling: false
            }
          },
          y: {
            tick: {
              format(x) {
                return numeral(x).format('0.00 b');
              }
            }
          }
        },
        grid: {
          x: { show: false },
          y: {
            show: true,
            lines: [{ value: 0 }]
          }
        },
        tooltip: { show: true }, // groups values into one tooltip.. turning it off makes it more difficult to view related values
        legend: { show: false },
        regions: []
      },
      loadingUsage: false,
      isLoadingMonthlyUsage: false,
      monthlyUsageRaw: [],
      usage: [],
      sortBy: 'name',
      sortDirection: 'desc',
    }
  },
  mixins: [idToNameParserMixin, exportCsvMixin, shortenProductNameMixin, dateUtilityMixin],
  async mounted() {
    this.initializeGraph();
    const providersToFetch = this.determineWhatToFetch();
    if (providersToFetch.length) {
      providersToFetch.forEach(async (provider) => {
        await this.fetchUsage(this.getMonthAndYear(this.selectedDate).month, this.getMonthAndYear(this.selectedDate).year, provider.provider, provider.type);
      });
    }
    this.fetchRemoteMonthlyUsage();
  },
  watch: {
    satUsage(newvalue) {
      if (newvalue) {
        this.loadData(newvalue, 'Satellite');
      } else {
        this.clearData('Satellite');
      }
    },
    cellUsage(newvalue) {
      if (newvalue) {
        this.loadData(newvalue, 'Cellular');
      } else {
        this.clearData('Cellular');
      }
    },
    activeSubscriptionsForTheSelectedMonth() {
      if (this.activeSubscriptionsForTheSelectedMonth.length == 0) {
        this.clearData('Satellite');
        this.clearData('Cellular');
      } 
    }
  },
  computed: {
    // VUEX getters
    remote() {
      return this.$store.state.remotes.remote;
    },
    subscriptions() {
      return this.$store.state.remotes.remote.subscriptions;
    },
    hasActiveCellSubscription() {
      return this.activeSubscriptionsForTheSelectedMonth.find(s => s.hasCellular) != null;
    },
    hasActiveSatSubscription() {
      return this.activeSubscriptionsForTheSelectedMonth.find(s => s.hasSatellite && !this.hasIntelsatSubscription) != null;
    },
    hasIntelsatSubscription() {
      return this.activeSubscriptionsForTheSelectedMonth.find(s => (s.productShortcode == 'PR_INTFLEX_5x2' || s.productShortcode == 'PR_VEL_KALO_4X1')) != null;
    },
    // This is the raw usage data from the vuex store
    satUsage() {
      if (!this.$store.getters.remoteSatelliteUsageDaily || !Object.keys(this.$store.getters.remoteSatelliteUsageDaily).length) return null;
      return this.$store.getters.remoteSatelliteUsageDaily;
    },
    cellUsage() {
      if (!this.$store.getters.remoteCellularUsageDaily || !Object.keys(this.$store.getters.remoteCellularUsageDaily).length) return null;
      return this.$store.getters.remoteCellularUsageDaily;
    },
    // This returns the subscription for the selectedDate
    activeSubscriptionsForTheSelectedMonth() {
      if (!this.subscriptions || !this.subscriptions.length || !this.selectedDate) return [];
      const month = this.selectedDate.getUTCMonth();
      const year = this.selectedDate.getUTCFullYear();
      return this.subscriptions.filter(s => {
        const subscriptionStartDate = new Date(s.startDate);
        const subscriptionEndDate = new Date(s.endDate);

        let monthIsBeforeOrEqual = false;
        if (subscriptionStartDate.getUTCFullYear() < year) { // if the year is earlier, obviously it's ok
          monthIsBeforeOrEqual = true;
        } else if (subscriptionStartDate.getUTCFullYear() == year) { // if it's the same year
          if (subscriptionStartDate.getUTCMonth() <= month) { // but the month is less or equal
            monthIsBeforeOrEqual = true;
          }
        }
        let monthIsAfterOrEqual = false;
        if (subscriptionEndDate.getUTCFullYear() > year) { // if the year is in the future, we're good
          monthIsAfterOrEqual = true;
        } else if (subscriptionEndDate.getUTCFullYear() == year) {
          if (subscriptionEndDate.getUTCMonth() >= month) { // but the month is greater or equal
            monthIsAfterOrEqual = true;
          }
        }
        return monthIsBeforeOrEqual && monthIsAfterOrEqual;
      });
    },
    usedCellData() { return this.getUsedDataForMonth(this.cellUsage) },
    usedSatData() { return this.getUsedDataForMonth(this.satUsage) },
    currentDateInUtc() {
      return this.getDateInUTCFormat(new Date());
    },
    monthlyUsage() {
      // Build the of months going back
      const monthArray = [];
      for (let i = 0; i < 12; i++) {
        // build the timestamp first
        const currentDate = endOfMonth(subMonths(this.currentDateInUtc, i));
        const currentDateString = format(currentDate, 'yyyy-MM');
        monthArray.push({
          timestamp: currentDateString,
          satUsage: 0,
          cellUsage: 0,
          subscriptions: this.subscriptions.filter(s => {
            // subscriptions which go through or end on the month being iterated over
            const subEndDate = new Date(s.endDate);
            const subStartDate = new Date(s.startDate);
            const interceptingDate = (isBefore(currentDate, subEndDate) && isAfter(currentDate, subStartDate));
            return interceptingDate || format(subEndDate, 'yyyy-MM') == format(currentDate, 'yyyy-MM');
          })
        })
      }

      this.monthlyUsageRaw.forEach(uo => {
        if (uo.satellite) {
          Object.keys(uo.satellite).forEach(key => {
            const value = uo.satellite[key];
            const idxOfMonth = monthArray.findIndex(s => {
              const formattedTimestamp = format(this.getDateInUTCFormat(new Date(s.timestamp)), 'yyyy-MM-\'01\' 00:00:00');
              return formattedTimestamp == key;
            });
            if (idxOfMonth == -1) return;
            monthArray[idxOfMonth].satUsage += value;
          });
        }
        if (uo.cellular) {
          Object.keys(uo.cellular).forEach(key => {
            const value = uo.cellular[key];
            const idxOfMonth = monthArray.findIndex(s => {
              const formattedTimestamp = format(this.getDateInUTCFormat(new Date(s.timestamp)), 'yyyy-MM-\'01\' 00:00:00');
              return formattedTimestamp == key;
            });
            if (idxOfMonth == -1) return;
            monthArray[idxOfMonth].cellUsage += value;
          });
        }
      });

      monthArray.sort((a, b) => {
        if (a[this.sortBy] < b[this.sortBy]) return this.sortDirection == 'asc' ? -1 : 1;
        if (a[this.sortBy] > b[this.sortBy]) return this.sortDirection == 'asc' ? 1 : -1;
        return 0;
      });

      return monthArray;
    }
  },
  methods: {
    // Initialize the graph
    initializeGraph() {
      this.setGraphDates();
    },
    getSatelliteProvider() {
      // exit early if intelsat
      if (this.hasIntelsatSubscription) return 'PV_INTSATFLEXMOVE';
      return 'PV_KYMETABROADBAND';
    },
    getCellularProvider() {
      return 'PV_CELLULAR';
    },
    getUsedDataForMonth(usage) {
      if (!usage) return 0;
      const allDaysValues = Object.values(usage);
      if (allDaysValues.length) return allDaysValues.reduce((a, c) => a + c);
      return 0;
    },
    determineWhatToFetch() {
      const providerArray = [];
      if (this.hasIntelsatSubscription) {
        providerArray.push({ type: 'Satellite', provider: 'PV_INTSATFLEXMOVE' });
      } else {
        if (this.hasActiveCellSubscription) {
          providerArray.push({ type: 'Cellular', provider: 'PV_CELLULAR' });
        }
        if (this.hasActiveSatSubscription) {
          providerArray.push({ type: 'Satellite', provider: 'PV_KYMETABROADBAND' });
        }
      }
      return providerArray;
    },
    transformUsageToGraphObject(usage) {
      if (!usage) return null;
      const labels = [];
      const series = [];
      Object.keys(usage).forEach(key => {
        const formattedKey = format(new Date(key), 'yyyy-MM-dd HH:mm:ss');
        labels.push(formattedKey);
        series.push(usage[key]);
      });
      return { labels, series };
    },
    loadData(dataToLoad, dataType) {
      // the x axis first value is always the 'name' of the axis, followed by the values
      // let xAxis = ["x"].concat();
      const days = this.daysInMonth();
      // build out the load object
      const loadObject = {
        columns: [
          [dataType] // the first value is always the name of the axis, it's the string that we pass in the computed watchers
        ],
        unload: false
      };

      // iterate through xAxes (days in the month) to associate satellite & cellular data
      // with their corresponding day
      const transformedData = this.transformUsageToGraphObject(dataToLoad);
      for (let i = 0; i < days.length; i++) {
        const dayOfMonth = days[i];

        // find the matching indicies for both data types (if they exist)
        const matchIndex = transformedData.labels.indexOf(dayOfMonth);

        // populate the columns with matches found & otherwise null so day is still presented on graph
        loadObject.columns[0].push(matchIndex != -1 ? transformedData.series[matchIndex] : null);
      }

      // load the graph with the usage
      this.$refs.usageGraph.load(loadObject);
    },
    clearData(dataType) {
      if (!this.$refs.usageGraph) return;
      this.$refs.usageGraph.clearRegions();
      this.$refs.usageGraph.unload({ ids: ['x', dataType] });
      this.initializeGraph();
    },
    exportGraph() {
      // build the export array
      const exportArray = [];

      if (this.satUsage) {
        for (var prop in this.satUsage) {
          const formattedTimestamp = format(new Date(prop), 'yyyy-MM-dd');
          exportArray.push([formattedTimestamp, this.satUsage[prop], 0]);
        }
      }
      if (this.cellUsage) {
        for (var prop in this.cellUsage) {
          const formattedTimestamp = format(new Date(prop), 'yyyy-MM-dd');
          const existingSatelliteTimestamp = exportArray.find(a => a[0] == formattedTimestamp);
          if (existingSatelliteTimestamp) { // already exists, just assign
            existingSatelliteTimestamp[2] = this.cellUsage[prop];
          } else {
            exportArray.push([formattedTimestamp, 0, this.cellUsage[prop]]);
          }
        }
      }

      // export
      this.exportCsvArray(['timestamp', 'satellite usage (bytes)', 'cellular usage (bytes)'], exportArray, `${this.remote.name}_Usage_${format(this.selectedDate, 'yy-MMMM')}`);
    },
    async fetchUsage(month, year, provider, dataType) {
      this.loadingUsage = true;
      // build date
      const date = format(new Date(year, month, 1), 'yyyy-MM-dd\'T\'00:00:00');
      const lastDay = format(lastDayOfMonth(new Date(year, month, 1)), 'yyyy-MM-dd\'T\'23:59:59');
      await this.$store.dispatch('fetchRemoteUsageDaily', {
        remoteIds: [this.remote.id], metrics: ['MT_TOTALUSAGE'], startDate: date, endDate: lastDay, groupBy: 'day', provider, type: dataType 
      });
      this.loadingUsage = false;
    },
    async valueChanged(date) {
      this.setGraphDates();
      const providers = this.determineWhatToFetch();
      providers.forEach(async (provider) => {
        await this.fetchUsage(this.getMonthAndYear(date).month, this.getMonthAndYear(date).year, provider.provider, provider.type);
      })
    },
    getMonthAndYear(date) {
      return { month: date.getUTCMonth(), year: date.getUTCFullYear() }
    },
    daysInMonth() {
      if (!this.selectedDate) return [];
      // Get how many days there are in the month we selected
      let daysInMonth = getDaysInMonth(this.selectedDate);
      // Instatiate the return array
      const arrDays = [];
      // While the day count is not 0
      while (daysInMonth) {
        // Get the date for the year, month
        const current = new Date(this.selectedDate.getUTCFullYear(), this.selectedDate.getUTCMonth(), daysInMonth);
        const date = `${format(current, 'yyyy-MM-dd HH:mm:ss')}`;
        arrDays.push(date);
        daysInMonth--;
      }
      return arrDays.reverse();
    },
    async fetchRemoteMonthlyUsage() {
      // Get the subscriptions we care about
      const currentDate = new Date();
      const yearAgo = subMonths(currentDate, 12);
      const subsWeCareAbout = this.subscriptions.filter(s => isAfter(Date.parse(s.endDate), yearAgo));

      // For each subscription (we need provider and data type) create a list of fetches
      const fetchesToMake = [];
      subsWeCareAbout.forEach(sub => {
        // provider logic: KB by default. If product is intelsat, then intelsat, otherwise get both cell and sat
        let provider = 'PV_KYMETABROADBAND';
        if (sub.productShortcode == 'PR_INTFLEX_5x2' || sub.productShortcode == 'PR_VEL_KALO_4X1') provider = 'PV_INTSATFLEXMOVE';
        // instantiate the request payload
        const fetchPayload = {
          remoteIds: [this.remote.id],
          metrics: ['MT_TOTALUSAGE'],
          // startDate: format(Date.parse(sub.startDate), 'yyyy-MM-dd\'T\'00:00:00\'Z\''), // CLDSRV-11418
          startDate: format(Date.parse(sub.startDate), 'yyyy-MM-\'01T\'00:00:00\'Z\''),
          endDate: format(Date.parse(sub.endDate), 'yyyy-MM-dd\'T\'23:59:59\'Z\''),
          groupBy: 'month',
          provider,
          type: 'Satellite'
        };
        // all subscriptions have satellite, so let's make that call first
        const satFetch = this.$store.dispatch('fetchRemoteUsageMonthly', fetchPayload);
        fetchesToMake.push(satFetch);
        // now see if the subscription also supports cellular (if it was intelsat, this will be skipped)
        if (sub.hasCellular) {
          const cellFetchPayload = { ...fetchPayload, provider: 'PV_CELLULAR', type: 'Cellular' };
          const cellFetch = this.$store.dispatch('fetchRemoteUsageMonthly', cellFetchPayload);
          fetchesToMake.push(cellFetch);
        }
      });

      this.isLoadingMonthlyUsage = true;
      // Use Promise.all to resolve all fetches
      const result = await Promise.all(fetchesToMake);
      this.isLoadingMonthlyUsage = false;
      this.monthlyUsageRaw = result;
    },
    onSort(field, order) {
      this.sortBy = field == 'name' ? 'timestamp' : field;
      this.sortDirection = order;
    },
    // eslint-disable-next-line consistent-return
    ifUsageExceedsLimit(used, limit) {
      // eslint-disable-next-line no-param-reassign
      const limitInB = limit * 1000 * 1000 * 1000;
      if (!!used && !!limit && !!limitInB && (used > limitInB)) return 'has-text-danger';
      return '';
    },
    setGraphDates() { 
      // get the days in this month
      const days = this.daysInMonth();
      // Initialize the X axis
      this.graphSettings.data.columns[0] = ['x'].concat(days);
      // Initialize the values for the X axis as all 0s for both Sat and Cell
      this.graphSettings.data.columns[1] = ['Satellite'].concat(days.map(() => 0));
      this.graphSettings.data.columns[2] = ['Cellular'].concat(days.map(() => 0));
      // Render the graph
      this.$refs.usageGraph.init(this.graphSettings);
    }
  },
  components: {
    Chart
  }
}
</script>
<style lang="scss" scoped>
  @import '../../styles/mixins';

  .kpt-remote-usage-container {
    @include display-grid;
    grid-template-columns: 300px 1fr;
    gap: 12px;
  }
</style>
